import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { User } from '../../../users/users.model';
import { UserService } from '../../../users/users.service';

@Component({
  selector: 'app-user-autocomplete',
  templateUrl: './user-autocomplete.component.html',
  styleUrls: ['./user-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserAutocompleteComponent),
      multi: true
    }
  ]
})
export class UserAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder: string = 'Buscar usuarios...';

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  public searchControl = new FormControl('');
  public filteredUsers: User[] = [];
  public selectedUsers: User[] = [];
  public isSearching = false;

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: User[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly userService: UserService) {}

  ngOnInit(): void {
    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: User[]): void {
    this.selectedUsers = value || [];
  }

  registerOnChange(fn: (value: User[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
      this.selectedUsers = [];
      this.searchControl.setValue('');
      this.searchInput.nativeElement.value = '';
    } else {
      this.searchControl.enable();
    }
  }

  public onUserSelected(user: User): void {
    if (!this.isUserAlreadySelected(user.id)) {
      this.selectedUsers.push(user);
      this.onChange(this.selectedUsers);
      this.searchControl.setValue('');
      this.searchInput.nativeElement.value = '';
      this.filteredUsers = [];
    }
  }

  public removeUser(userId: string): void {
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId);
    this.onChange(this.selectedUsers);
    this.onTouched();
  }

  public displayUserName(user: User): string {
    return user ? user.client : '';
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(term => {
        if (typeof term === 'string') {
          this.onSearchInput(term);
        }
      });
  }

  private onSearchInput(term: string): void {
    if (!term || term.length < 2) {
      this.filteredUsers = [];
      return;
    }

    this.isSearching = true;
    this.userService.getAllUsers(term, '', true)
      .then(users => {
        this.filteredUsers = users.filter(
          user => !this.isUserAlreadySelected(user.id)
        );
      })
      .catch(error => {
        console.error('Error searching users:', error);
        this.filteredUsers = [];
      })
      .finally(() => {
        this.isSearching = false;
      });
  }

  private isUserAlreadySelected(userId: string): boolean {
    return this.selectedUsers.some(user => user.id === userId);
  }
}

